import gsap from "gsap";
import windowWidth from "../helpers/WindowWidth";
import { lock, unlock } from "tua-body-scroll-lock";

/**
 * "smart" navigation which goes off screen when scrolling down for a better overview of content and UX
 * navigation appears when scrolling up
 */
export default class NavigationController {
    constructor() {
        /**
         * Navigation DOM selectors
         * Navigation DOM state CSS classes
         * @type {{mobileNavigation: string, navigation: string, mobileNavigationItem: string, mobileNavigationTrigger: string, states: {navigationSlideUp: string, navigationScrolled: string, isActive: string, isAnimating: string, navigationFixed: string, mobileNavigationOpen: string}}}
         */
        this.DOM = {
            navigation: ".js-navigation-wrapper",
            mobileNavigation: ".js-mobile-navigation-wrapper",
            mobileNavigationTrigger: ".js-mobile-navigation-trigger",
            mobileNavigationItem: ".js-mobile-navigation-item",
            states: {
                navigationScrolled: "has-scrolled",
                navigationFixed: "is-fixed",
                navigationSlideUp: "slide-up",
                mobileNavigationOpen: "is-mobile-navigation-open",
                isActive: "is-active",
                isAnimating: "is-animating",
            },
        };

        /**
         * flag, state variable for scrolling event
         * @type {boolean}
         */
        this.scrolling = false;

        /**
         * amount of pixels to scroll from top for adding "has-scrolled" state class
         * @type {number}
         */
        this.scrollNavigationOffset = 200;

        /**
         * variable for storing amount of scroll from top position value
         * @type {number}
         */
        this.previousTop = 0;

        /**
         * variable for storing current scroll position value
         * @type {number}
         */
        this.currentTop = 0;
        this.scrollDelta = 0;
        this.scrollOffset = 0;

        /**
         * fetch navigation element DOM element
         * @type {Element}
         */
        this.navigation = document.querySelector(this.DOM.navigation);

        /**
         * fetch mobile navigation element DOM element
         * @type {Element}
         */
        this.mobileNavigation = document.querySelector(
            this.DOM.mobileNavigation,
        );

        /**
         * fetch mobile navigation trigger element DOM element
         * @type {Element}
         */
        this.mobileNavigationTrigger = document.querySelector(
            this.DOM.mobileNavigationTrigger,
        );

        /**
         * fetch mobile navigation item element DOM element
         * @type {Element}
         */
        this.mobileNavigationItem = document.querySelectorAll(
            this.DOM.mobileNavigationItem,
        );
    }

    /**
     *
     */
    init() {
        if (this.navigation !== null && windowWidth() > 800) {
            console.log("Navigation init()");
            this.navigationController();
        }

        if (this.mobileNavigation !== null) {
            console.log("Mobile navigation init()");
            this.mobileMenuControl();
        }
    }

    /**
     *
     */
    navigationController() {
        document.addEventListener("scroll", () => {
            if (!this.scrolling) {
                this.scrolling = true;

                if (!window.requestAnimationFrame) {
                    setTimeout(this.checkScroll(), 250);
                } else {
                    requestAnimationFrame(() => this.checkScroll());
                }
            }
        });
    }

    /**
     *
     */
    checkScroll() {
        /**
         *
         * @type {number}
         */
        let currentTop = window.pageYOffset | document.body.scrollTop;

        this.changeNavigationState(currentTop);

        this.previousTop = currentTop;
        this.scrolling = false;
    }

    /**
     *
     * @param currentTop
     */
    changeNavigationState(currentTop) {
        if (currentTop > this.scrollNavigationOffset) {
            this.navigation.classList.add(this.DOM.states.navigationScrolled);
        } else {
            this.navigation.classList.remove(
                this.DOM.states.navigationScrolled,
            );
        }

        if (this.previousTop >= currentTop) {
            this.scrollingUp(currentTop);
        } else {
            this.scrollingDown(currentTop);
        }
    }

    /**
     *
     * @param currentTop
     */
    scrollingUp(currentTop) {
        if (currentTop < this.scrollNavigationOffset) {
            this.navigation.classList.remove(this.DOM.states.navigationSlideUp);
        } else if (this.previousTop - currentTop > this.scrollDelta) {
            this.navigation.classList.remove(this.DOM.states.navigationSlideUp);
        }
    }

    /**
     *
     * @param currentTop
     */
    scrollingDown(currentTop) {
        if (currentTop > this.scrollNavigationOffset + this.scrollOffset) {
            this.navigation.classList.add(this.DOM.states.navigationSlideUp);
        } else if (currentTop > this.scrollNavigationOffset) {
            this.navigation.classList.remove(this.DOM.states.navigationSlideUp);
        }
    }

    /**
     * Control mobile menu
     */
    mobileMenuControl() {
        if (
            this.mobileNavigation === null ||
            this.mobileNavigationTrigger === null
        ) {
            return;
        }
        this.mobileNavigationTrigger.addEventListener("click", (event) => {
            event.preventDefault();
            if (
                !document.body.classList.contains(
                    this.DOM.states.mobileNavigationOpen,
                )
            ) {
                this.openMobileMenu();
            } else {
                if (
                    !document.body.classList.contains(
                        this.DOM.states.isAnimating,
                    )
                ) {
                    this.closeMobileMenu();
                }
            }
        });
    }

    /**
     * Open mobile menu
     * Lock scroll
     */
    openMobileMenu() {
        lock(this.mobileNavigation);
        this.mobileNavigationTrigger.classList.add(this.DOM.states.isActive);
        document.body.classList.add(this.DOM.states.mobileNavigationOpen);
        document.body.classList.add(this.DOM.states.isAnimating);
        gsap.to(this.mobileNavigation, {
            duration: 0.7,
            autoAlpha: 1,
            ease: "power3.inOut",
        });
        gsap.to(this.mobileNavigationItem, {
            duration: 1,
            autoAlpha: 1,
            x: "0%",
            delay: 0.1,
            ease: "power3.out",
            stagger: 0.1,
            onComplete: () => {
                document.body.classList.remove(this.DOM.states.isAnimating);
            },
        });
    }

    /**
     * Close mobile menu
     * Unlock scroll
     */
    closeMobileMenu() {
        gsap.to([this.mobileNavigationItem, this.mobileNavigation], {
            duration: 0.2,
            autoAlpha: 0,
            ease: "power3.inOut",
            onComplete: () => {
                document.body.classList.remove(
                    this.DOM.states.mobileNavigationOpen,
                );
                this.mobileNavigationTrigger.classList.remove(
                    this.DOM.states.isActive,
                );
                gsap.set([this.mobileNavigation, this.mobileNavigationItem], {
                    clearProps: "all",
                });
                unlock(this.mobileNavigation);
            },
        });
    }
}
